import type { StaleUpstreamKind } from '@sigora/domain';

/**
 * What the Audience step says about an audience waiting on a change above it, being updated for
 * one, or updated: one module, so the product copy is one file to change.
 */

/** What each upstream is called in these sentences. */
const UPSTREAM_WORDS: Record<StaleUpstreamKind, string> = {
  goal: 'goal',
  materials: 'materials',
  rqs: 'questions',
  dg: 'guide',
  audience: 'audience',
};

const joined = (words: readonly string[]): string =>
  words.length <= 1
    ? (words[0] ?? '')
    : `${words.slice(0, -1).join(', ')} and ${words.at(-1) as string}`;

/** "your changed guide and questions", for the kinds an audience is waiting on or answered. */
const changed = (kinds: readonly StaleUpstreamKind[]): string =>
  `your changed ${joined(kinds.map((kind) => UPSTREAM_WORDS[kind]))}`;

/** While Sigora is updating the audience, over the earlier version. */
export const audienceUpdatingNotice = (kinds: readonly StaleUpstreamKind[]): string =>
  `Sigora is updating your audience for ${changed(kinds)}…`;

/** The label over the audience shown while it waits on a change: the version before it. */
export const AUDIENCE_BEFORE_CHANGE = 'Before your change';

/** When the update has run past the step's wait. */
export const AUDIENCE_UPDATE_OVERDUE =
  'Sigora is taking longer than it should to update your audience.';

/** When the update failed, beside "Try again". */
export const AUDIENCE_UPDATE_FAILED = 'Sigora couldn’t update your audience. Try again.';

/** When the update waits on the guide being approved again. */
export const AUDIENCE_NEEDS_GUIDE =
  'Approve your guide first. Sigora updates the audience from it.';

/** Over an updated audience that changed. */
export const audienceUpdatedNotice = (kinds: readonly StaleUpstreamKind[]): string =>
  `Sigora updated this audience for ${changed(kinds)}.`;

/** Over an updated audience that did not need to change. */
export const audienceUnchangedNotice = (kinds: readonly StaleUpstreamKind[]): string =>
  `Sigora checked this against ${changed(kinds)}. Nothing needed to change. Press Continue to approve it.`;

/** The heading over what an update took out. */
export const AUDIENCE_REMOVED_HEADING = 'Taken out:';

/** The tag on an item an update changed or added. */
export const AUDIENCE_MARKS = { changed: 'Changed', added: 'New' } as const;
